import React, { FC, useState, useEffect, useContext } from 'react';

import AddIcon from '@material-ui/icons/AddCircle';

import { GlobalContext } from '../../../../../../store';
import { ICourse } from '../../../../../../store/admin/courses/store';
import { IRoundTeesAssignation } from '../../../../../../store/roundTees/store';

import Button from '../../../../../../components/Button';
import TeeAssignationRow from './Components/TeeAssignationRow/TeeAssignationRow';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';
import { TeeBoxRowsStyled } from './TeeBoxRowsStyled.Styles';
import TeeBoxRowsTitle from './Components/TeeBoxRowsTitle';
import { getEmptyRoundTeeAssignation } from './Utils/getEmptyRoundTeeAssignation';
import { isRoundTeeAssignationInvalid } from './Utils/isRoundTeeAssignationValid';
import { sortBy } from '../../../../../../contrib/sortBy';

interface IProps {
  onNotSavedChange: (flightsNotSaved: boolean) => void;
  course?: ICourse;
}

const TeeBoxRows: FC<IProps> = props => {
  const { onNotSavedChange, course } = props;

  const { clubId, eventId, roundId } = useRouteInfoState();

  const {
    actions: {
      notifications: { pushError, pushSuccess },
      roundTees: { getRoundTees, updateRoundTee, createRoundTee, deleteRoundTee, removeRoundTee }
    },
    roundTees: { assignation: teeAssignation }
  } = useContext(GlobalContext);

  const [roundTeeAssignations, setRoundTeeAssignations] = useState<
    Array<Partial<IRoundTeesAssignation>>
  >([]);
  const [unsavedRoundTeeAssignations, setUnsavedRoundTeeAssignations] = useState<number[]>([]);
  const [newRoundTeeAssignation, setNewRoundTeeAssignation] = useState<IRoundTeesAssignation>();
  const [displayNewRoundTeeAssignation, setDisplayNewRoundTeeAssignation] = useState<boolean>(
    false
  );

  const onRoundTeeAssignationChange = (field: string, index: number) => (evnt: any): void => {
    const value = evnt.target ? evnt.target.value : evnt.value;

    const notSaved = [...unsavedRoundTeeAssignations];

    const isIn = notSaved.find((i: number) => i === index);

    if (!Number.isNaN(value)) {
      if (isIn === undefined) {
        notSaved.push(index);
      }

      const newRoundTeeAssignations = [...sortBy(teeAssignation, 'asc', 'id')];

      // @ts-ignore
      newRoundTeeAssignations[index][field as keyof IRoundTeesAssignation] = value;

      setRoundTeeAssignations(newRoundTeeAssignations);
      setUnsavedRoundTeeAssignations(notSaved);
    }
  };

  const onNewRoundTeeAssignationChange = (field: string) => (evnt: any): void => {
    if (newRoundTeeAssignation) {
      const value = evnt.target ? evnt.target.value : evnt.value;

      setNewRoundTeeAssignation({ ...newRoundTeeAssignation, [field]: value });
      setUnsavedRoundTeeAssignations([...unsavedRoundTeeAssignations, -1]);

      onNotSavedChange(!allRoundTeeAssignationsAreValid());
    }
  };

  const deleteTeeAssignation = (
    index: number,
    roundTeeAssignation: IRoundTeesAssignation
  ) => async () => {
    if (roundTeeAssignation.id >= 0) {
      const response: any = await deleteRoundTee(
        Number(clubId),
        Number(eventId),
        Number(roundId),
        roundTeeAssignation.id
      );

      if (response) {
        removeRoundTee(roundTeeAssignation.id).catch();
      }
    } else {
      setDisplayNewRoundTeeAssignation(false);

      setNewRoundTeeAssignation(undefined);
    }

    const notSaved: number[] = unsavedRoundTeeAssignations.filter((i: number) => i !== index);
    setUnsavedRoundTeeAssignations(notSaved);
    onNotSavedChange(notSaved.length > 0);
  };

  const removeNewTeeBoxAssignation = () => () => {
    setDisplayNewRoundTeeAssignation(false);
    setNewRoundTeeAssignation(undefined);

    setUnsavedRoundTeeAssignations(
      unsavedRoundTeeAssignations.filter((index: number) => index !== -1)
    );
  };

  const addNewTeeBoxAssignment = (): void => {
    const notSaved = [...unsavedRoundTeeAssignations, -1];

    setRoundTeeAssignations(roundTeeAssignations);
    setUnsavedRoundTeeAssignations(notSaved);

    if (roundId) {
      setNewRoundTeeAssignation(getEmptyRoundTeeAssignation(roundId));
    }

    setDisplayNewRoundTeeAssignation(true);

    onNotSavedChange(notSaved.length > 0);
  };

  const allRoundTeeAssignationsAreValid = (): boolean => {
    const teeAssignationsToValidate =
      displayNewRoundTeeAssignation && newRoundTeeAssignation
        ? [...teeAssignation, newRoundTeeAssignation]
        : [...teeAssignation];

    return teeAssignationsToValidate.reduce(
      (allFlightsValid: boolean, teeAssignatin) =>
        allFlightsValid && !isRoundTeeAssignationInvalid(teeAssignatin),
      true
    );
  };

  const roundTeeAssignationSetup = async (roundTeesAssignation: IRoundTeesAssignation) => {
    const { ageLower, ageUpper, gender, handicapLower, handicapUpper } = roundTeesAssignation;

    const newRoundTeesAssignation: IRoundTeesAssignation = {
      ...roundTeesAssignation,
      ageLower: ageLower ? ageLower : null,
      ageUpper: ageUpper ? ageUpper : null,
      gender: gender && gender !== 'B' ? gender : null,
      handicapLower: handicapLower ? handicapLower : null,
      handicapUpper: handicapUpper ? handicapUpper : null
    };

    const roundTeeFunction =
      !newRoundTeesAssignation.id || newRoundTeesAssignation.id < 0
        ? createRoundTee
        : updateRoundTee;

    const response = await roundTeeFunction(
      Number(clubId),
      Number(eventId),
      Number(roundId),
      newRoundTeesAssignation
    );

    if (response.error) {
      pushError('The assignation could not be made');
      return false;
    } else {
      pushSuccess('The assignation was made successfully');
      return true;
    }
  };

  const saveRoundTeeAssignation = (
    index: number,
    roundTeeAssignation: IRoundTeesAssignation
  ) => async (): Promise<any> => {
    const isANewRoundTeeAssignation = roundTeeAssignation.id < 0;

    if (isANewRoundTeeAssignation) {
      delete roundTeeAssignation.id;
    }

    const response = await roundTeeAssignationSetup(roundTeeAssignation);

    if (!!response) {
      const notSaved: number[] = unsavedRoundTeeAssignations.filter(
        (i: number) => i !== index && index !== -1
      );

      if (isANewRoundTeeAssignation) {
        setDisplayNewRoundTeeAssignation(false);

        setNewRoundTeeAssignation(undefined);
      }

      setUnsavedRoundTeeAssignations(notSaved);

      onNotSavedChange(notSaved.length > 0);
    }
  };

  useEffect(() => {
    if (clubId && eventId && roundId) {
      getRoundTees(Number(clubId), Number(eventId), Number(roundId)).catch();
    }
  }, [clubId, eventId, roundId]);

  return (
    <TeeBoxRowsStyled className="admin-form-radios column flights">
      {teeAssignation.length > 0 || newRoundTeeAssignation ? <TeeBoxRowsTitle /> : <></>}

      {sortBy(teeAssignation, 'asc', 'id').map(
        (teeAssignatin: IRoundTeesAssignation, index: number) => (
          <TeeAssignationRow
            deleteTeeBoxAssignment={deleteTeeAssignation}
            teeAssignation={teeAssignatin}
            onRoundTeeAssignationChange={onRoundTeeAssignationChange}
            index={index}
            key={`flight-item-${index}`}
            saveTeeBoxAssignment={saveRoundTeeAssignation}
            unsavedRoundTeeAssignations={unsavedRoundTeeAssignations}
            course={course}
          />
        )
      )}

      {displayNewRoundTeeAssignation && newRoundTeeAssignation && (
        <TeeAssignationRow
          deleteTeeBoxAssignment={removeNewTeeBoxAssignation}
          teeAssignation={newRoundTeeAssignation}
          onRoundTeeAssignationChange={onNewRoundTeeAssignationChange}
          index={-1}
          saveTeeBoxAssignment={saveRoundTeeAssignation}
          unsavedRoundTeeAssignations={unsavedRoundTeeAssignations}
          course={course}
        />
      )}

      <Button
        color="primary"
        onClick={addNewTeeBoxAssignment}
        className="add-button"
        disabled={unsavedRoundTeeAssignations.length > 0}
      >
        <AddIcon color="primary" />

        <span className="add-button-text">ADD ANOTHER ITEM</span>
      </Button>
    </TeeBoxRowsStyled>
  );
};

export default TeeBoxRows;
